import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { NoData } from "src/shared/components/tables/no-data";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { useTranslation } from "react-i18next";
import { Box, Button, Stack, TablePagination, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { InventoryPermissions } from "src/constants/permissions";
import { oneBranchHasPermission } from "src/utils";
import { useGetPoRequests } from "src/api/inventory/reorder";
import SecondaryChip from "src/shared/components/secondary-chip";
import TableActions from "./table-actions";
import FilterDrawer from "./filters";
import FilterIcon from "src/assets/svg-icons/aggregators/filter.svg";
import { Link as RouterLink } from "react-router-dom";
import Link from "@mui/material/Link";

const EntriesList = () => {
  const { t } = useTranslation("purchasing");
  const { t: inventoryT } = useTranslation("inventory");
  const { t: generalT } = useTranslation("general");
  const [open, setOpen] = useState(false);
  const [branchId, setBranchId] = useState("");
  const [refNum, setRefNum] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [status, setStatus] = useState("all");

  const [pageNumber, setPageNumber] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const navigate = useNavigate();

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const HeadText = [
    generalT("reference"),
    t("branch"),
    t("supplier"),
    t("items"),
    inventoryT("expectedTotal"),
    generalT("status"),
    inventoryT("sentStatus"),
    generalT("createdBy"),
    generalT("createdAt"),
    generalT("actions"),
  ];

  const filters = {
    page: pageNumber + 1,
    ...(!!refNum && { reference_number: refNum }),
    ...(!!supplierId && { supplier_id: supplierId }),
    ...(!!branchId && { branch_id: branchId }),
    ...(!!status && status !== "all" && { status: status }),
  };

  // APIS
  const { data, isFetching, refetch } = useGetPoRequests(filters);

  const addPermission = oneBranchHasPermission(
    InventoryPermissions.addUnitCostAdjustment,
  );

  const emptyDataArr = data?.data.length === 0;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPageNumber(newPage);
  };

  useEffect(() => {
    if (!!data) {
      setTotal(data.meta?.total as number);
    }
  }, [data]);

  return (
    <Stack spacing={3} maxWidth={{ xs: "100%", md: `calc(100vw - 316px)` }}>
      <Box display={"flex"} justifyContent={"flex-end"}>
        <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
          <Button
            color="tertiary"
            variant="outlined"
            endIcon={<img src={FilterIcon} height={"16px"} alt="" />}
            onClick={handleOpen}
          >
            {t("filter")}
          </Button>
        </Stack>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              {HeadText.map((item) => (
                <TableCell
                  key={item}
                  align={"center"}
                  sx={{
                    bgcolor: "#F9FAFB",
                  }}
                >
                  <Typography
                    fontSize={"12px"}
                    color={"#475467"}
                    lineHeight={"18px"}
                    fontWeight={500}
                  >
                    {item}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isFetching || !data ? (
            <TableLoadingSkeleton rowsLength={15} cellsLength={10} />
          ) : (
            <TableBody>
              {data.data.map((row: any) => (
                <TableRow
                  key={row.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell align="center">
                    {" "}
                    <Link
                      component={RouterLink}
                      to={`/inventory/reorder/po/${row.id}`}
                      underline="always"
                      sx={{ fontSize: "inherit" }}
                    >
                      {row.reference}
                    </Link>
                  </TableCell>
                  <TableCell align="center">{row.branch?.name}</TableCell>
                  <TableCell align="center">{row.supplier?.name}</TableCell>
                  <TableCell align="center">{row.items_count}</TableCell>
                  <TableCell align="center">{row.items_total}</TableCell>
                  <TableCell align="center">
                    <SecondaryChip
                      label={row.status}
                      variant={
                        row.status === "Received"
                          ? "success"
                          : row.status === "Canceled"
                            ? "error"
                            : row.status === "Not Received"
                              ? "warning"
                              : "warning"
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    {row.send_method === "email"
                      ? inventoryT("sentEmail")
                      : row.send_method === "app"
                        ? inventoryT("sentApp")
                        : inventoryT("notSent")}
                  </TableCell>
                  <TableCell align="center">{row.created_by?.name}</TableCell>
                  <TableCell align="center">{row.created_at}</TableCell>
                  <TableCell align="center">
                    <TableActions row={row} refetch={refetch} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
        {emptyDataArr && <NoData />}
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[20]}
        component="div"
        count={total}
        rowsPerPage={20}
        page={pageNumber}
        onPageChange={handleChangePage}
        showFirstButton
        showLastButton
      />

      {/* Filter Drawer */}
      <FilterDrawer
        open={open}
        setBranchId={setBranchId}
        branchId={branchId}
        refNum={refNum}
        setRefNum={setRefNum}
        handleClose={handleClose}
        supplierId={supplierId}
        setSupplierId={setSupplierId}
        status={status}
        setStatus={setStatus}
      />
    </Stack>
  );
};
export default EntriesList;
